import { useState } from 'react';
import { Box } from '@mui/material';
import { useAuthClientJwtQuery } from 'api/queries/auth';
import { useMapsGoogleConfigurationQuery } from 'api/queries/config';
import { useSubscription } from 'react-stomp-hooks';
import { BookingItem, VehicleLocation } from 'utils/data/data-types';
import GoogleMapReact from 'google-map-react';
import { useQueryClient } from '@tanstack/react-query';
import { operatorsQueryKeys, useOperatorsVehiclesLocationsQuery } from 'api/queries/operators';
import { OperatorsVehiclesLocationsQueryParams } from 'api/types';
import { colors } from 'remote/global';
import WrappedOrderDetailsModal from 'components/modals/OrderModal/WrappedOrderDetailsModal';
import BusIcon from '@/assets/Buss_icon.svg?react';

const defaultProps = {
  center: {
    lat: 59.41,
    lng: 24.77,
  },
  zoom: 11,
};

interface MapVehicleMarkerProps {
  onClick?: () => void;
  [key: string]: any;
  zoom: number;
  text: string;
}

function MapVehicleMarker({ onClick, zoom, text }: MapVehicleMarkerProps) {
  return (
    <Box
      sx={{
        position: 'absolute',
        zIndex: 99,
        color: 'white',
        padding: '5px 10px',
        '&:hover': {
          cursor: 'pointer',
        },
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-40px',
          transform: 'translate(-50%, -100%)',
          background: 'white',
          padding: '4px',
          borderRadius: '4px',
          color: colors.uiBlue600,
          fontSize: zoom < 11 ? '11px' : '13px',
          fontWeight: 500,
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '50%',
            marginLeft: '-5px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderTop: '5px solid white',
          },
        }}
      >
        {text}
      </Box>
      <BusIcon
        style={{
          width: zoom < 11 ? '50px' : '70px',
          height: '40px',
          transform: 'translate(-50%, -100%)',
        }}
      />
    </Box>
  );
}

function LogisticsMapView() {
  const [zoom, setZoom] = useState(11);
  const { data: googleConfig } = useMapsGoogleConfigurationQuery();
  const { data: userData } = useAuthClientJwtQuery();
  const operatorId = userData?.['husky.operatorId']!;
  const [isOrderDetailsModalOpen, setIsOrderDetailsModalOpen] = useState(false);
  const [bookingItem, setBookingItem] = useState<BookingItem | undefined>();
  const [queryParams, setQueryParams] = useState<OperatorsVehiclesLocationsQueryParams>();
  const queryClient = useQueryClient();
  const operatorsVehiclesLocationsQuery = useOperatorsVehiclesLocationsQuery(queryParams);
  const { data: activeVehicles } = operatorsVehiclesLocationsQuery;

  useSubscription(`/topic/vehicle-locations/operator/${operatorId}`, (message) => {
    const activeVehicle: VehicleLocation = JSON.parse(message.body);
    queryClient.setQueryData(
      operatorsQueryKeys.vehiclesLocationsFilter(operatorId, queryParams!),
      (prev: VehicleLocation[]) =>
        prev.map((el) => {
          if (el.vehicleId === activeVehicle.vehicleId) return activeVehicle;
          return el;
        })
    );
  });

  const handleVehicleMarkersRender = async (map: any) => {
    const bounds = map.getBounds();
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const maxLat = ne.lat();
    const maxLon = ne.lng();
    const minLat = sw.lat();
    const minLon = sw.lng();
    setQueryParams((p) => ({ ...p, maxLat, maxLon, minLat, minLon }));
  };

  // TODO: Accept bookingNumber instead of orderId, also pass rideNumber
  const handleRideSelect = async (orderId: number) => {
    setBookingItem({ bookingNumber: String(orderId), rideNumber: 1 });
    setIsOrderDetailsModalOpen(true);
  };

  if (!googleConfig?.feApiKey) return null;

  const handleClearOrderDetailsModalState = () => {
    setIsOrderDetailsModalOpen(false);
    setBookingItem(undefined);
  };

  return (
    <>
      <WrappedOrderDetailsModal
        open={isOrderDetailsModalOpen}
        setOpen={setIsOrderDetailsModalOpen}
        onClose={handleClearOrderDetailsModalState}
        bookingNumber={bookingItem?.bookingNumber!}
        rideId={bookingItem?.rideNumber!}
      />
      <Box sx={{ width: '100%', height: '100%', display: 'flex' }}>
        <GoogleMapReact
          style={{ flex: 1 }}
          bootstrapURLKeys={{
            key: googleConfig?.feApiKey || '',
          }}
          yesIWantToUseGoogleMapApiInternals
          onZoomAnimationEnd={(value) => setZoom(value)}
          onGoogleApiLoaded={({ map }) => handleVehicleMarkersRender(map)}
          defaultCenter={defaultProps.center}
          defaultZoom={defaultProps.zoom}
        >
          {activeVehicles?.map(({ coords, vehicleId, licensePlate, orderId }) => (
            <MapVehicleMarker
              onClick={() => handleRideSelect(orderId)}
              key={vehicleId}
              lat={coords.lat}
              lng={coords.lon}
              zoom={zoom}
              text={licensePlate}
            />
          ))}
        </GoogleMapReact>
      </Box>
    </>
  );
}

export default LogisticsMapView;
